<script>
import Vue from 'vue';
import {mapState, mapActions, mapGetters} from 'vuex';
import {convertDaysObject} from "../../services/ConverterService";

export default Vue.extend({
    filters: {
        lowercase(string) {
            return string.toLowerCase()
        }
    },
    computed: {
        ...mapState({
            editMode: state => state.account.notifications.editMode,
            userNotifications: state => state.account.notifications.item,
            originalNotifications: state => state.account.notifications.originalItem,
            supplier: state => state.auth.supplier
        }),
        ...mapGetters({
            activeWeekdays: 'account/activeWeekdays'
        }),
    },
    methods: {
        ...mapActions({
            toggleNotifications: 'account/toggleNotifications',
            updateNotificationsLevel: 'account/updateNotificationsLevel',
            editNotificationsLevel: 'account/editNotificationsLevel',
            toggleNotificationEditMode: 'account/toggleNotificationsEditMode'
        }),
        cancelEditNotifications() {
            this.editNotificationsLevel({item: this.originalNotifications});
            this.toggleNotificationEditMode(false);
        },
        edit(obj) {
            // If set to fortnightly then set non-selected days to false
            if(this.userNotifications.fortnightly && obj.hasOwnProperty('days')) {
                this.editNotificationsLevel({item: convertDaysObject(obj)})
            } else {
                // Check if switching from weekly to fortnightly, if so then reset days to set monday true
                if(!obj.hasOwnProperty('days')) {
                    this.editNotificationsLevel({item: obj});
                    this.editNotificationsLevel({item: convertDaysObject({days:{monday: true}})})
                } else {
                    this.editNotificationsLevel({item: obj})
                }
            }
        }
    }
});
</script>

<template>
    <div class="account-notifications">
        <div class="account-notifications__heading">
            <div v-if="editMode" class="account-notifications__header">
                {{ $t('admin.profile.notifications.heading.header') }}
            </div>
        </div>
        <div class="account-notifications__box account-notifications__notification-box">
            <div class="account-notifications__notifications-fields">
                <div class="account-notifications__display">
                    <div v-if="!editMode"
                         class="account-notifications__notification-status">
                        <div v-if="userNotifications"
                             class="account-notifications__display-checkbox">
                            <label for="account-notifications__active">{{ $t('admin.profile.receive') }} </label>
                            <div id="account-notifications__active"
                                 :class="{'account-notifications__checkbox--checked': userNotifications.active}"
                                 class="account-notifications__checkbox"
                                 @click="toggleNotifications(supplier.id)"/>
                        </div>
                        <div v-if="!userNotifications.active"
                             class="account-notifications__display-no-notice">
                            <strong>{{ $t('admin.profile.noNotice') }}</strong>
                        </div>
                        <div v-if="userNotifications.active"
                             class="account-notifications__display-notice">
                            <span class="account-notifications__display-notice">
                                {{ $t('admin.profile.notice') }}
                            </span>
                            <span v-if="Object.keys(activeWeekdays).length === 7"
                                  class="account-notifications__days">
                                <span class="account-notifications__display-notice">
                                    {{ $t('admin.profile.everyday') }}
                                </span>
                            </span>
                            <span v-if="Object.keys(activeWeekdays).length < 7"
                                  class="account-notifications__days">
                                <span v-for="(boolean, day, index) in activeWeekdays"
                                      :key="index">
                                    <span v-if="index !== (Object.keys(activeWeekdays).length - 1)">
                                        <span>{{ $t('admin.profile.notifications.days.' + day).toLowerCase() }}</span>
                                        <span v-if="index !== (Object.keys(activeWeekdays).length - 2)">,</span>
                                    </span>
                                    <span v-if="index === (Object.keys(activeWeekdays).length - 1)">
                                        <span v-if="Object.keys(activeWeekdays).length > 1"> {{ $t('admin.profile.and') }} </span>
                                        <span>{{ $t('admin.profile.notifications.days.' + day).toLowerCase() }}</span>
                                    </span>
                                </span>
                                <span v-if="userNotifications.weekly">
                                    {{ $t('admin.profile.weekly') }}
                                </span>
                                <span v-if="userNotifications.fortnightly">
                                    {{ $t('admin.profile.fortnightly') }}
                                </span>
                            </span>
                            <div class="account-notifications__display-edit"
                                 @click="toggleNotificationEditMode">
                                <i class="fa fa-edit"/> {{ $t('admin.profile.notifications.edit') }}
                            </div>
                        </div>
                    </div>
                </div>
                <div v-if="editMode" class="account-notifications__notification-edit">
                    <div class="account-notifications__notification-days">
                        <div class="account-notifications__notification-checkbox">
                            <label for="account-notifications__monday"
                                   class="account-notifications__notification-label">
                                {{ $t('admin.profile.notifications.days.monday') }}
                            </label>
                            <div id="account-notifications__monday"
                                 :class="{'account-notifications__checkbox--checked': userNotifications.days.monday}"
                                 class="account-notifications__checkbox"
                                 @click="edit({days: {monday: !userNotifications.days.monday}})"/>
                        </div>
                        <div class="account-notifications__notification-checkbox">
                            <label for="account-notifications__tuesday"
                                   class="account-notifications__notification-label">
                                {{ $t('admin.profile.notifications.days.tuesday') }}
                            </label>
                            <div id="account-notifications__tuesday"
                                 :class="{'account-notifications__checkbox--checked': userNotifications.days.tuesday}"
                                 class="account-notifications__checkbox"
                                 @click="edit({days: {tuesday: !userNotifications.days.tuesday}})"/>
                        </div>
                        <div class="account-notifications__notification-checkbox">
                            <label for="account-notifications__wednesday"
                                   class="account-notifications__notification-label">
                                {{ $t('admin.profile.notifications.days.wednesday') }}
                            </label>
                            <div id="account-notifications__wednesday"
                                 :class="{'account-notifications__checkbox--checked': userNotifications.days.wednesday}"
                                 class="account-notifications__checkbox"
                                 @click="edit({days: {wednesday: !userNotifications.days.wednesday}})"/>
                        </div>
                        <div class="account-notifications__notification-checkbox">
                            <label for="account-notifications__thursday"
                                   class="account-notifications__notification-label">
                                {{ $t('admin.profile.notifications.days.thursday') }}
                            </label>
                            <div id="account-notifications__thursday"
                                 :class="{'account-notifications__checkbox--checked': userNotifications.days.thursday}"
                                 class="account-notifications__checkbox"
                                 @click="edit({days: {thursday: !userNotifications.days.thursday}})"/>
                        </div>
                        <div class="account-notifications__notification-checkbox">
                            <label for="account-notifications__friday"
                                   class="account-notifications__notification-label">
                                {{ $t('admin.profile.notifications.days.friday') }}
                            </label>
                            <div id="account-notifications__friday"
                                 :class="{'account-notifications__checkbox--checked': userNotifications.days.friday}"
                                 class="account-notifications__checkbox"
                                 @click="edit({days: {friday: !userNotifications.days.friday}})"/>
                        </div>
                        <div class="account-notifications__notification-checkbox">
                            <label for="account-notifications__saturday"
                                   class="account-notifications__notification-label">
                                {{ $t('admin.profile.notifications.days.saturday') }}
                            </label>
                            <div id="account-notifications__saturday"
                                 :class="{'account-notifications__checkbox--checked': userNotifications.days.saturday}"
                                 class="account-notifications__checkbox"
                                 @click="edit({days: {saturday: !userNotifications.days.saturday}})"/>
                        </div>
                        <div class="account-notifications__notification-checkbox">
                            <label for="account-notifications__sunday"
                                   class="account-notifications__notification-label">
                                {{ $t('admin.profile.notifications.days.sunday') }}
                            </label>
                            <div id="account-notifications__sunday"
                                 :class="{'account-notifications__checkbox--checked': userNotifications.days.sunday}"
                                 class="account-notifications__checkbox"
                                 @click="edit({days: {sunday: !userNotifications.days.sunday}})"/>
                        </div>
                    </div>
                    <div class="account-notifications__notification-week">
                        <div class="account-notifications__notification-checkbox">
                            <label for="account-notifications__weekly"
                                   class="account-notifications__notification-label">
                                {{ $t('admin.profile.notifications.weekly') }}
                            </label>
                            <div id="account-notifications__weekly"
                                 :class="{'account-notifications__checkbox--checked': userNotifications.weekly}"
                                 class="account-notifications__checkbox"
                                 @click="edit({weekly: true, fortnightly: false})"/>
                        </div>
                        <div class="account-notifications__notification-checkbox">
                            <label for="account-notifications__fortnightly"
                                   class="account-notifications__notification-label">
                                {{ $t('admin.profile.notifications.fortnightly') }}
                            </label>
                            <div id="account-notifications__fortnightly"
                                 :class="{'account-notifications__checkbox--checked': userNotifications.fortnightly}"
                                 class="account-notifications__checkbox"
                                 @click="edit({weekly: false, fortnightly: true})"/>
                        </div>
                    </div>
                    <div class="account-notifications__notification-buttons">
                        <button class="account-notifications__btn"
                                @click="updateNotificationsLevel({admin: true, item: userNotifications})">
                            {{ $t('admin.profile.notifications.update') }}
                        </button>
                        <button class="account-notifications__btn"
                                @click="cancelEditNotifications">
                            {{ $t('admin.profile.notifications.cancel') }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>



// WEBPACK FOOTER //
// src/components/AccountNotifications/AccountNotifications.vue